import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { Fragment, memo, useCallback } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { css, jsx } from '@emotion/react';
import { ACTION, ACTION_SUBJECT } from '@atlaskit/editor-common/analytics';
import { usePortalProvider } from '@atlaskit/editor-common/portal';
import { BaseTheme, IntlProviderIfMissingWrapper, WidthProvider } from '@atlaskit/editor-common/ui';
import { fg } from '@atlaskit/platform-feature-flags';
import ErrorBoundary from '../create-editor/ErrorBoundary';
import { createFeatureFlagsFromProps } from '../create-editor/feature-flags-from-props';
import ReactEditorView from '../create-editor/ReactEditorView';
import { ContextAdapter } from '../nodeviews/context-adapter';
import { useSetPresetContext } from '../presets/context';
import EditorContext from '../ui/EditorContext';
import { RenderTracking } from '../utils/performance/components/RenderTracking';
import { useProviders } from './hooks/useProviders';
import { getBaseFontSize } from './utils/getBaseFontSize';
/**
 * EditorInternalComponent is used to capture the common component
 * from the `render` method of `Editor` and share it with `EditorNext`.
 */
export var EditorInternal = /*#__PURE__*/memo(function (_ref) {
  var _props$performanceTra, _props$performanceTra2;
  var props = _ref.props,
    handleAnalyticsEvent = _ref.handleAnalyticsEvent,
    createAnalyticsEvent = _ref.createAnalyticsEvent,
    handleSave = _ref.handleSave,
    editorActions = _ref.editorActions,
    providerFactory = _ref.providerFactory,
    onEditorCreated = _ref.onEditorCreated,
    onEditorDestroyed = _ref.onEditorDestroyed,
    preset = _ref.preset,
    AppearanceComponent = _ref.AppearanceComponent;
  var setEditorApi = useCallback(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function (api) {
    // This is an workaround to unblock Editor Lego Decoupling project, if you have questions ping us #cc-editor-lego
    // We may clean up this code when EditorActions deprecation process starts
    // @ts-expect-error 2339: Property '__EDITOR_INTERNALS_DO_NOT_USE__API' does not exist on type 'EditorActions<any>'.
    editorActions.__EDITOR_INTERNALS_DO_NOT_USE__API = api;
  }, [editorActions]);
  var overriddenEditorProps = _objectSpread(_objectSpread({}, props), {}, {
    onSave: props.onSave ? handleSave : undefined,
    // noop all analytic events, even if a handler is still passed.
    analyticsHandler: undefined
  });
  var featureFlags = createFeatureFlagsFromProps(props);
  var renderTracking = (_props$performanceTra = props.performanceTracking) === null || _props$performanceTra === void 0 || (_props$performanceTra = _props$performanceTra.renderTracking) === null || _props$performanceTra === void 0 ? void 0 : _props$performanceTra.editor;
  var renderTrackingEnabled = renderTracking === null || renderTracking === void 0 ? void 0 : renderTracking.enabled;
  var useShallow = renderTracking === null || renderTracking === void 0 ? void 0 : renderTracking.useShallow;
  var _usePortalProvider = usePortalProvider(),
    _usePortalProvider2 = _slicedToArray(_usePortalProvider, 2),
    portalProviderAPI = _usePortalProvider2[0],
    PortalRenderer = _usePortalProvider2[1];

  // ED-16320: Check for explicit disable so that by default
  // it will still be enabled as it currently is. Then we can
  // progressively opt out synthetic tenants.
  var isErrorTrackingExplicitlyDisabled = ((_props$performanceTra2 = props.performanceTracking) === null || _props$performanceTra2 === void 0 || (_props$performanceTra2 = _props$performanceTra2.errorTracking) === null || _props$performanceTra2 === void 0 ? void 0 : _props$performanceTra2.enabled) === false;
  return jsx(Fragment, null, renderTrackingEnabled && jsx(RenderTracking, {
    componentProps: props,
    action: ACTION.RE_RENDERED,
    actionSubject: ACTION_SUBJECT.EDITOR,
    handleAnalyticsEvent: handleAnalyticsEvent,
    propsToIgnore: ['defaultValue'],
    useShallow: useShallow
  }), jsx(ErrorBoundary, {
    errorTracking: !isErrorTrackingExplicitlyDisabled,
    createAnalyticsEvent: createAnalyticsEvent,
    contextIdentifierProvider: props.contextIdentifierProvider,
    featureFlags: featureFlags
  }, jsx(WidthProvider, {
    css: css({
      height: '100%'
    })
  }, jsx(EditorContext, {
    editorActions: editorActions
  }, jsx(ContextAdapter, null, jsx(IntlProviderIfMissingWrapper, null, jsx(Fragment, null, jsx(ReactEditorViewContextWrapper, {
    editorProps: overriddenEditorProps,
    createAnalyticsEvent: createAnalyticsEvent,
    portalProviderAPI: portalProviderAPI,
    providerFactory: providerFactory,
    onEditorCreated: onEditorCreated,
    onEditorDestroyed: onEditorDestroyed,
    disabled: props.disabled,
    preset: preset,
    setEditorApi: setEditorApi,
    render: function render(_ref2) {
      var _props$featureFlags, _props$featureFlags2;
      var editor = _ref2.editor,
        view = _ref2.view,
        eventDispatcher = _ref2.eventDispatcher,
        config = _ref2.config,
        dispatchAnalyticsEvent = _ref2.dispatchAnalyticsEvent,
        editorRef = _ref2.editorRef,
        editorAPI = _ref2.editorAPI;
      return jsx(BaseTheme, {
        baseFontSize: getBaseFontSize(props.appearance)
      }, jsx(AppearanceComponent, {
        innerRef: editorRef,
        editorAPI: editorAPI,
        appearance: props.appearance,
        disabled: props.disabled,
        editorActions: editorActions,
        editorDOMElement: editor,
        editorView: view,
        providerFactory: providerFactory,
        eventDispatcher: eventDispatcher,
        dispatchAnalyticsEvent: dispatchAnalyticsEvent,
        maxHeight: props.maxHeight,
        minHeight: props.minHeight,
        onSave: props.onSave ? handleSave : undefined,
        onCancel: props.onCancel,
        popupsMountPoint: props.popupsMountPoint,
        popupsBoundariesElement: props.popupsBoundariesElement,
        popupsScrollableElement: props.popupsScrollableElement,
        contentComponents: config.contentComponents,
        primaryToolbarComponents: config.primaryToolbarComponents,
        primaryToolbarIconBefore: props.primaryToolbarIconBefore,
        secondaryToolbarComponents: config.secondaryToolbarComponents,
        customContentComponents: props.contentComponents,
        customPrimaryToolbarComponents: props.primaryToolbarComponents,
        customSecondaryToolbarComponents: props.secondaryToolbarComponents,
        contextPanel: props.contextPanel,
        collabEdit: props.collabEdit,
        persistScrollGutter: props.persistScrollGutter,
        enableToolbarMinWidth: ((_props$featureFlags = props.featureFlags) === null || _props$featureFlags === void 0 ? void 0 : _props$featureFlags.toolbarMinWidthOverflow) != null ? !!((_props$featureFlags2 = props.featureFlags) !== null && _props$featureFlags2 !== void 0 && _props$featureFlags2.toolbarMinWidthOverflow) : props.allowUndoRedoButtons,
        useStickyToolbar: props.useStickyToolbar,
        featureFlags: featureFlags,
        pluginHooks: config.pluginHooks,
        hideAvatarGroup: fg('platform_editor_remove_hide_avatar_group_prop') ? undefined : props.hideAvatarGroup
      }));
    }
  }), jsx(PortalRenderer, null))))))));
});
function ReactEditorViewContextWrapper(props) {
  var _props$editorProps$me;
  var setInternalEditorAPI = useSetPresetContext();
  var setExternalEditorAPI = props.setEditorApi;

  /**
   * We use the context to retrieve the editorAPI
   * externally for consumers via `usePreset`.
   *
   * However we also may need to retrieve this value internally via context
   * so we should also set the value for the `EditorContext` that is used in
   * `EditorInternal`.
   */
  var setEditorAPI = useCallback(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function (api) {
    setInternalEditorAPI === null || setInternalEditorAPI === void 0 || setInternalEditorAPI(api);
    setExternalEditorAPI === null || setExternalEditorAPI === void 0 || setExternalEditorAPI(api);
  }, [setInternalEditorAPI, setExternalEditorAPI]);
  useProviders({
    contextIdentifierProvider: props.editorProps.contextIdentifierProvider,
    mediaProvider: (_props$editorProps$me = props.editorProps.media) === null || _props$editorProps$me === void 0 ? void 0 : _props$editorProps$me.provider
  });
  return jsx(ReactEditorView, _extends({}, props, {
    setEditorApi: setEditorAPI
  }));
}